import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Divider,
  Switch,
  CircularProgress,
} from "@mui/material";
import {
  Email as EmailIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NudgeIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import AdherenceThresholdEditor from "./AdherenceThresholdEditor";
import { authenticatedApi } from "../../utils/api";
import { apiService } from "../../config/api";

interface PreferencesPanelProps {
  studyId: string;
  onClose?: () => void;
}

interface NotificationPreferences {
  alert_digest_enabled: boolean;
  nudge_emails_enabled: boolean;
  enrollment_notifications_enabled: boolean;
  deletion_notifications_enabled: boolean;
  historical_extraction_notifications_enabled: boolean;
}

interface PreferenceRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: React.ReactNode;
}

const PreferenceRow: React.FC<PreferenceRowProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      py: 2.5,
      px: 3,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: "action.hover",
        flexShrink: 0,
        color: "text.secondary",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {description}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  </Box>
);

const PreferencesPanel: React.FC<PreferencesPanelProps> = ({ studyId }) => {
  const [showThresholdEditor, setShowThresholdEditor] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailNotifsExpanded, setEmailNotifsExpanded] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>({
    alert_digest_enabled: false,
    nudge_emails_enabled: false,
    enrollment_notifications_enabled: true,
    deletion_notifications_enabled: true,
    historical_extraction_notifications_enabled: true,
  });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await apiService.getAuthToken();
        if (!token) return;
        const res = await authenticatedApi.get<{ success: boolean; preferences: NotificationPreferences }>(
          `/api/notification-preferences/${studyId}`,
          token
        );
        if (res.success) {
          setNotifPrefs(res.preferences);
        }
      } catch {
        // silently fall back to defaults
      } finally {
        setNotifLoading(false);
      }
    };
    load();
  }, [studyId]);

  const handleToggle = async (key: keyof NotificationPreferences) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifSaving(key);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const res = await authenticatedApi.put<{ success: boolean; preferences: NotificationPreferences }>(
        `/api/notification-preferences/${studyId}`,
        next,
        token
      );
      if (res.success) {
        setNotifPrefs(res.preferences);
      }
    } catch {
      setError("Failed to save notification preference. Please try again.");
    } finally {
      setNotifSaving(null);
    }
  };

  const handleOpenTemplateEditor = () => {
    window.open(`/researcher/template-editor?study=${studyId}`, "_blank");
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 3, letterSpacing: -0.5 }}
      >
        Study Preferences
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card
        variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden" }}
      >
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <PreferenceRow
            icon={<EmailIcon fontSize="small" />}
            title="Email Templates"
            description="Manage templates for participant alerts and notifications"
            action={
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="small" />}
                onClick={handleOpenTemplateEditor}
                sx={{ textTransform: "none", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Open Editor
              </Button>
            }
          />
          <Divider />
          <PreferenceRow
            icon={<SettingsIcon fontSize="small" />}
            title="Adherence Thresholds"
            description="Define minimum daily metrics required for participant adherence"
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => setShowThresholdEditor(true)}
                sx={{ textTransform: "none", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                Edit
              </Button>
            }
          />
          <Divider />
          <Box
            sx={{
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => setEmailNotifsExpanded(!emailNotifsExpanded)}
          >
            <PreferenceRow
              icon={<EmailIcon fontSize="small" />}
              title="Email Notifications"
              description="Manage email notification preferences"
              action={
                emailNotifsExpanded ? (
                  <ExpandLessIcon color="action" />
                ) : (
                  <ExpandMoreIcon color="action" />
                )
              }
            />
          </Box>

          {emailNotifsExpanded && (
            <Box sx={{ pl: 6, pr: 3, pb: 2 }}>
              <Box sx={{ py: 1.5 }}>
                <PreferenceRow
                  icon={<NotificationsIcon fontSize="small" />}
                  title="Alert Digest Emails"
                  description="Receive daily email summaries when participants show low adherence"
                  action={
                    notifLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={notifPrefs.alert_digest_enabled}
                        onChange={() => handleToggle("alert_digest_enabled")}
                        disabled={notifSaving === "alert_digest_enabled"}
                        size="small"
                      />
                    )
                  }
                />
              </Box>
              <Box sx={{ py: 1.5 }}>
                <PreferenceRow
                  icon={<NudgeIcon fontSize="small" />}
                  title="Nudge Emails"
                  description="Receive follow-up reminders when critical alerts remain unreviewed"
                  action={
                    notifLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={notifPrefs.nudge_emails_enabled}
                        onChange={() => handleToggle("nudge_emails_enabled")}
                        disabled={notifSaving === "nudge_emails_enabled"}
                        size="small"
                      />
                    )
                  }
                />
              </Box>
              <Box sx={{ py: 1.5 }}>
                <PreferenceRow
                  icon={<PersonAddIcon fontSize="small" />}
                  title="Enrollment Notifications"
                  description="Receive an email when a new participant completes enrollment in this study"
                  action={
                    notifLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={notifPrefs.enrollment_notifications_enabled}
                        onChange={() => handleToggle("enrollment_notifications_enabled")}
                        disabled={notifSaving === "enrollment_notifications_enabled"}
                        size="small"
                      />
                    )
                  }
                />
              </Box>
              <Box sx={{ py: 1.5 }}>
                <PreferenceRow
                  icon={<DeleteIcon fontSize="small" />}
                  title="Deletion Notifications"
                  description="Receive an email when a participant is deleted from this study"
                  action={
                    notifLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={notifPrefs.deletion_notifications_enabled}
                        onChange={() => handleToggle("deletion_notifications_enabled")}
                        disabled={notifSaving === "deletion_notifications_enabled"}
                        size="small"
                      />
                    )
                  }
                />
              </Box>
              <Box sx={{ py: 1.5 }}>
                <PreferenceRow
                  icon={<HistoryIcon fontSize="small" />}
                  title="Historical Extraction Notifications"
                  description="Receive an email when historical data extraction completes or fails for a participant"
                  action={
                    notifLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={notifPrefs.historical_extraction_notifications_enabled}
                        onChange={() => handleToggle("historical_extraction_notifications_enabled")}
                        disabled={notifSaving === "historical_extraction_notifications_enabled"}
                        size="small"
                      />
                    )
                  }
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {showThresholdEditor && (
        <AdherenceThresholdEditor
          open={showThresholdEditor}
          studyId={studyId}
          onClose={() => setShowThresholdEditor(false)}
        />
      )}
    </Box>
  );
};

export default PreferencesPanel;
